import type Phaser from "phaser";

/**
 * Local-only "脚下秘境" — the ground pattern a player sees under the world.
 * Patterns are procedurally generated, seamlessly tileable textures rendered
 * through a TileSprite. Old `ink` storage values migrate to the default.
 * This is a personal visual preference and is never synced to other players.
 */
export const TERRAIN_PATTERN_IDS = ["grid", "hex", "dot"] as const;

export type TerrainPatternId = (typeof TERRAIN_PATTERN_IDS)[number];

export const DEFAULT_TERRAIN_PATTERN: TerrainPatternId = "grid";

export function isTerrainPatternId(value: unknown): value is TerrainPatternId {
  return (
    typeof value === "string" &&
    (TERRAIN_PATTERN_IDS as readonly string[]).includes(value)
  );
}

export function normalizeTerrainPattern(value: unknown): TerrainPatternId {
  return isTerrainPatternId(value) ? value : DEFAULT_TERRAIN_PATTERN;
}

export interface StoredTerrainPattern {
  id: TerrainPatternId;
  shouldPersist: boolean;
}

export function normalizeStoredTerrainPattern(
  value: string | null,
): StoredTerrainPattern {
  const id = normalizeTerrainPattern(value);
  return {
    id,
    shouldPersist: value !== null && value !== id,
  };
}

export function terrainPatternTextureKey(id: TerrainPatternId): string {
  return `terrain-pattern-${id}`;
}

const LINE_COLOR = 0x83b0c6;
const LINE_ALPHA = 0.17;
const ACCENT_COLOR = 0xb99146;

interface TileSpec {
  width: number;
  height: number;
  draw: (graphics: Phaser.GameObjects.Graphics, w: number, h: number) => void;
}

const GRID_CELL = 56;
const DOT_CELL = 44;
const HEX_SIZE = 26; // center-to-vertex; flat-top hex

const TILE_SPECS: Record<TerrainPatternId, TileSpec> = {
  grid: {
    width: GRID_CELL,
    height: GRID_CELL,
    draw: (g, w, h) => {
      // Only the top and left edges; tiling completes the grid so shared
      // edges are never doubled.
      g.lineStyle(1, LINE_COLOR, LINE_ALPHA);
      g.lineBetween(0, 0, w, 0);
      g.lineBetween(0, 0, 0, h);
      g.fillStyle(ACCENT_COLOR, 0.14);
      g.fillCircle(0, 0, 1.3);
    },
  },
  dot: {
    width: DOT_CELL,
    height: DOT_CELL,
    draw: (g, w, h) => {
      g.fillStyle(LINE_COLOR, 0.24);
      g.fillCircle(w / 2, h / 2, 1.9);
      g.fillStyle(ACCENT_COLOR, 0.12);
      g.fillCircle(0, 0, 1.2);
    },
  },
  hex: {
    // Flat-top honeycomb. Unit cell: width 3s, height sqrt(3)*s.
    width: Math.round(3 * HEX_SIZE),
    height: Math.round(Math.sqrt(3) * HEX_SIZE),
    draw: (g) => {
      g.lineStyle(1, LINE_COLOR, LINE_ALPHA);
      const s = HEX_SIZE;
      const rowStep = Math.sqrt(3) * s;
      // Draw every hex overlapping the unit cell (plus neighbours) so the
      // generated texture wraps seamlessly.
      for (let col = -1; col <= 3; col += 1) {
        const cx = 1.5 * s * col;
        const yOffset = (((col % 2) + 2) % 2) * (rowStep / 2);
        for (let row = -1; row <= 2; row += 1) {
          drawFlatTopHex(g, cx, rowStep * row + yOffset, s);
        }
      }
    },
  },
};

function drawFlatTopHex(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  s: number,
): void {
  const points: { x: number; y: number }[] = [];
  for (let i = 0; i < 6; i += 1) {
    const angle = (Math.PI / 3) * i;
    points.push({ x: cx + s * Math.cos(angle), y: cy + s * Math.sin(angle) });
  }
  g.beginPath();
  g.moveTo(points[0]!.x, points[0]!.y);
  for (let i = 1; i < points.length; i += 1) {
    g.lineTo(points[i]!.x, points[i]!.y);
  }
  g.closePath();
  g.strokePath();
}

/**
 * Ensure the tileable texture for a pattern exists in the scene's texture
 * manager, generating it once.
 */
export function ensureTerrainPatternTexture(
  scene: Phaser.Scene,
  id: TerrainPatternId,
): string {
  const key = terrainPatternTextureKey(id);
  if (scene.textures.exists(key)) {
    return key;
  }
  const spec = TILE_SPECS[id];
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  spec.draw(graphics, spec.width, spec.height);
  graphics.generateTexture(key, spec.width, spec.height);
  graphics.destroy();
  return key;
}
